// Low-level helpers that write SEO tags into <head>. Shared by usePageTitle,
// RouteSeo and detail pages so every public page emits the same tag set.
import { BASE_URL, getPageMeta } from './seoMeta';

export const DEFAULT_OG_IMAGE = 'https://media.base44.com/images/public/69e561e7a8d8686b80fb4d20/ff541ed73_generated_image.png';

// Plain-text excerpt (markdown stripped) cut on a word boundary, max ~158 chars.
export function excerpt(text, max = 158) {
  if (!text) return '';
  const plain = text.replace(/[*_#>`[\]]/g, '').replace(/\(https?:[^)]*\)/g, '').replace(/\s+/g, ' ').trim();
  if (plain.length <= max) return plain;
  const cut = plain.lastIndexOf(' ', max - 1);
  return plain.slice(0, cut > 100 ? cut : max - 1).replace(/[,;:–-]+$/, '') + '…';
}

export function setMeta(attr, key, content) {
  const matches = document.head.querySelectorAll(`meta[${attr}="${key}"]`);
  matches.forEach((node, i) => { if (i > 0) node.remove(); });
  let el = matches[0];
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function removeMeta(attr, key) {
  document.head.querySelectorAll(`meta[${attr}="${key}"]`).forEach((el) => el.remove());
}

function setLink(rel, href, hreflang) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  const all = document.head.querySelectorAll(selector);
  all.forEach((el, i) => { if (i > 0) el.remove(); });
  let el = all[0];
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function cleanUrl(pathname) {
  const p = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return p === '/' ? BASE_URL : `${BASE_URL}${p}`;
}

// URL-bound tags: canonical, og:url, hreflang, <html lang>. Language is
// toggled client-side (same URL), so pl/en/x-default all point to the clean URL.
export function applyUrlTags(pathname, language) {
  const url = cleanUrl(pathname);
  // /terpeny/{id}/odmiany duplicates the parent terpene page — canonical to the parent.
  const odmianyMatch = pathname.match(/^\/terpeny\/([^/]+)\/odmiany\/?$/);
  setLink('canonical', odmianyMatch ? `${BASE_URL}/terpeny/${odmianyMatch[1]}` : url);
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:type', url === BASE_URL ? 'website' : 'article');
  setMeta('property', 'og:site_name', 'TerpeneAtlas');
  setMeta('property', 'og:locale', language === 'pl' ? 'pl_PL' : 'en_US');
  setMeta('name', 'twitter:card', 'summary_large_image');
  setLink('alternate', url, 'pl');
  setLink('alternate', url, 'en');
  setLink('alternate', url, 'x-default');
  document.documentElement.lang = language;
}

export function applySeoTags({ title, description, image, language, pathname }) {
  // Exiting pages and late data responses must not write to the next route.
  if (cleanUrl(pathname) !== cleanUrl(window.location.pathname)) return;
  const meta = getPageMeta(pathname, language);
  title = meta?.title || title;
  const ogImage = image || meta?.image || DEFAULT_OG_IMAGE;
  document.head.querySelectorAll('title').forEach((node, i) => { if (i > 0) node.remove(); });
  document.title = title;
  setMeta('name', 'description', description);
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:image', ogImage);
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'twitter:image', ogImage);
  applyUrlTags(pathname, language);
}

export function setNoindex(on) {
  if (on) setMeta('name', 'robots', 'noindex, nofollow');
  else removeMeta('name', 'robots');
}